import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Bell, CheckCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { HandoffReasonBadge, timeAgo } from '@/components/notifications/handoffUtils';

const POLL_MS = 20000;

export const NotificationBell = () => {
  const [data, setData] = useState({ unread: 0, open: 0, latest: [] });
  const [open, setOpen] = useState(false);
  const lastSeen = useRef(sessionStorage.getItem('hc_handoff_seen'));
  const navigate = useNavigate();

  const goTo = useCallback((h) => {
    api.post(`/handoffs/${h.id}/read`).catch(() => {});
    setOpen(false);
    navigate(`/tickets?ticket=${h.ticket_id}`);
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/handoffs/summary');
      setData(d);
      const newest = d.latest[0]?.event_at;
      if (lastSeen.current !== null) {
        d.latest.filter((h) => !h.read && h.event_at > lastSeen.current).slice(0, 3).forEach((h) => {
          toast(`Hand-off: ${h.patient_name || 'Patient'}`, {
            description: `${h.reminders ? 'Reminder · ' : ''}${h.reason_label}${h.note ? ` — ${h.note}` : ''}`,
            duration: 15000,
            action: { label: 'Open', onClick: () => goTo(h) },
          });
        });
      }
      if (newest && (!lastSeen.current || newest > lastSeen.current)) lastSeen.current = newest;
      if (lastSeen.current === null) lastSeen.current = '';
      sessionStorage.setItem('hc_handoff_seen', lastSeen.current);
    } catch { /* ignore polling errors */ }
  }, [goTo]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => { clearInterval(t); };
  }, [load]);

  const readAll = async () => { await api.post('/handoffs/read-all'); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-stone-100 transition-colors">
          <Bell className="w-5 h-5 text-stone-700" />
          {data.unread > 0 && (
            <span data-testid="notification-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-700 text-white text-[10px] font-bold flex items-center justify-center">
              {data.unread > 99 ? '99+' : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
          <div>
            <div className="text-sm font-semibold">Hand-offs</div>
            <div className="text-xs text-stone-500" data-testid="notification-open-count">{data.open} open</div>
          </div>
          <button data-testid="notification-read-all" onClick={readAll} className="text-xs text-stone-600 hover:text-stone-900 flex items-center gap-1">
            <CheckCheck className="w-3.5 h-3.5" /> Mark all read
          </button>
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {data.latest.length === 0 && <div className="p-6 text-sm text-stone-500 text-center" data-testid="notification-empty">No hand-offs yet</div>}
          {data.latest.map((h) => (
            <button key={h.id} data-testid={`notification-item-${h.id}`} onClick={() => goTo(h)}
              className={`w-full text-left px-4 py-3 border-b border-stone-100 hover:bg-stone-50 transition-colors ${h.read ? '' : 'bg-rose-50/60'}`}>
              <div className="flex items-center gap-2">
                {!h.read && <span className="w-2 h-2 rounded-full bg-rose-700 shrink-0" />}
                <span className="text-sm font-medium truncate">{h.patient_name || 'Patient'}</span>
                <span className="ml-auto text-[11px] text-stone-500 shrink-0">{timeAgo(h.event_at)}</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <HandoffReasonBadge reason={h.reason} label={h.reason_label} />
                {h.status === 'done' && <span className="text-[10px] text-emerald-700 font-semibold uppercase">Done</span>}
                {h.reminders > 0 && h.status === 'open' && <span className="text-[10px] text-amber-700 font-semibold uppercase">Overdue</span>}
              </div>
              {h.note && <div className="mt-1 text-xs text-stone-600 line-clamp-2">{h.note}</div>}
            </button>
          ))}
        </div>
        <button data-testid="notification-view-all" onClick={() => { setOpen(false); navigate('/handoffs'); }}
          className="w-full py-2.5 text-sm font-medium text-rose-800 hover:bg-stone-50">View all hand-offs</button>
      </PopoverContent>
    </Popover>
  );
};
